import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import { ThalesCrossedDiagram } from "../../../../components/diagrams/ThalesTriangleDiagram";
import { makeSimpleClosedTask, normalizeMay2026Variant, pick } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const VARIANTS = [
  { OA: 10, OB: 4, OC: 5, OD: 8 },
  { OA: 15, OB: 6, OC: 10, OD: 9 },
  { OA: 18, OB: 8, OC: 12, OD: 12 },
  { OA: 14, OB: 5, OC: 7, OD: 10 },
  { OA: 16, OB: 9, OC: 12, OD: 12 },
  { OA: 20, OB: 8, OC: 16, OD: 10 },
  { OA: 21, OB: 10, OC: 14, OD: 15 },
  { OA: 24, OB: 9, OC: 18, OD: 12 },
  { OA: 9, OB: 5, OC: 3, OD: 15 },
  { OA: 12, OB: 7, OC: 6, OD: 14 },
  { OA: 25, OB: 6, OC: 15, OD: 10 },
  { OA: 18, OB: 5, OC: 9, OD: 10 },
];

const ORIGINAL_OPTIONS = [
  ["A", "4"],
  ["B", "9"],
  ["C", "10"],
  ["D", "16"],
];

const OPTION_LABELS = ["A", "B", "C", "D"];

function buildWrongAnswers(data) {
  const candidates = [
    data.OC,
    data.OA,
    data.OB,
    data.OA - data.OB,
    data.OC + data.OB,
    data.OD + 2,
    data.OD - 1,
  ];
  return [...new Set(candidates)]
    .filter((value) => Number.isInteger(value) && value > 0 && value !== data.OD)
    .slice(0, 3)
    .map(String);
}

function buildSortedOptions(data) {
  return [data.OD, ...buildWrongAnswers(data).map(Number)]
    .sort((first, second) => first - second)
    .map((value, index) => [OPTION_LABELS[index], String(value)]);
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const data = variant === "original" ? { OA: 12, OB: 6, OC: 8, OD: 9 } : pick(VARIANTS);
  return makeSimpleClosedTask({
    type: "MaturaParallelLinesSegmentsClosedTask",
    taskNumber: "20",
    variant,
    prompt: null,
    correct: String(data.OD),
    wrong: [],
    fixedOptions: variant === "original" ? ORIGINAL_OPTIONS : buildSortedOptions(data),
    tags: ["planimetria", "twierdzenie Talesa"],
    data: { ...data, kind: "parallelSegments" },
    explanationSteps: [
      { title: "Trójkąty OAD i OCB są podobne, więc odpowiadające odcinki są proporcjonalne.", latex: "\\frac{|OA|}{|OC|}=\\frac{|OD|}{|OB|}" },
      { title: "Podstawiamy długości i rozwiązujemy proporcję.", latex: `\\frac{${data.OA}}{${data.OC}}=\\frac{|OD|}{${data.OB}}\\Longrightarrow |OD|=${data.OD}` },
    ],
  });
}

export function renderTask({ data }) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p>
        Na płaszczyźnie dane są cztery proste: <InlineMath math="k" />,{" "}
        <InlineMath math="l" />, <InlineMath math="m" /> oraz <InlineMath math="n" />.
        Proste <InlineMath math="k" /> oraz <InlineMath math="l" /> są równoległe.
        Prosta <InlineMath math="m" /> przecina proste <InlineMath math="k" /> i{" "}
        <InlineMath math="l" /> odpowiednio w punktach <InlineMath math="A" /> i{" "}
        <InlineMath math="C" />, a prosta <InlineMath math="n" /> odpowiednio w punktach{" "}
        <InlineMath math="D" /> i <InlineMath math="B" />. Odcinki <InlineMath math="AC" /> i{" "}
        <InlineMath math="BD" /> przecinają się w punkcie <InlineMath math="O" />.
        Ponadto <InlineMath math={`|OA|=${data.OA}`} />,{" "}
        <InlineMath math={`|OB|=${data.OB}`} /> oraz{" "}
        <InlineMath math={`|OC|=${data.OC}`} /> (zobacz rysunek).
      </p>

      <ThalesCrossedDiagram labels={{ OA: data.OA, OB: data.OB, OC: data.OC }} />

      <p>{data.instruction}</p>
      <p>
        Odcinek <InlineMath math="OD" /> ma długość:
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        {data.options.map((option) => (
          <div key={option.label}>
            <InlineMath math={`${option.label}.\\;${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const option = data.options?.[data.correctIndex];

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div>
        <p>
          <strong>Krok 1.</strong> Rozpatrujemy trójkąty <InlineMath math={"\\triangle OAD"} /> i{" "}
          <InlineMath math={"\\triangle OCB"} />. Są podobne, ponieważ:
        </p>
        <ul style={{ paddingLeft: "1.4rem", margin: "0.35rem 0" }}>
          <li><InlineMath math={"\\angle OAD=\\angle OCB"} /> — kąty naprzemianległe przy prostych równoległych <InlineMath math={"k\\parallel l"} />;</li>
          <li><InlineMath math={"\\angle ODA=\\angle OBC"} /> — druga para kątów naprzemianległych;</li>
          <li><InlineMath math={"\\angle AOD=\\angle COB"} /> — kąty wierzchołkowe.</li>
        </ul>
        <ThalesCrossedDiagram
          labels={{ OA: data.OA, OB: data.OB, OC: data.OC }}
          showSimilarAngles
        />
      </div>

      <div>
        <p><strong>Krok 2.</strong> Z podobieństwa odpowiadają sobie boki <InlineMath math={"OA\\leftrightarrow OC"} /> oraz <InlineMath math={"OD\\leftrightarrow OB"} />, więc:</p>
        <BlockMath math={"\\frac{|OA|}{|OC|}=\\frac{|OD|}{|OB|}"} />
      </div>

      <div>
        <p><strong>Krok 3.</strong> Podstawiamy dane i rozwiązujemy proporcję:</p>
        <BlockMath math={"\\frac{" + data.OA + "}{" + data.OC + "}=\\frac{|OD|}{" + data.OB + "}"} />
        <BlockMath math={data.OA + "\\cdot" + data.OB + "=" + data.OC + "\\cdot|OD|"} />
        <BlockMath math={`|OD|=${data.OD}`} />
      </div>

      {option && <p><strong>Wniosek.</strong> Poprawna odpowiedź: <InlineMath math={option.label + ".\\;" + option.latex} />.</p>}
    </div>
  );
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedInput as renderInput,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
